import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Table,
  Badge,
  Button,
  Input,
  Progress,
} from "reactstrap";
import CommonHeader from "components/Headers/CommonHeader.js";
import LoadingDots from "components/Common/LoadingDots";
import Swal from "sweetalert2";
import {
  FaDollarSign,
  FaStar,
  FaMapMarkerAlt,
  FaDownload,
  FaCrown,
  FaCompass,
} from "react-icons/fa";

const CATEGORY_DISTRIBUTION = [
  { name: "Food Expert", share: 28, color: "primary", count: "340 Tours" },
  { name: "History Guide", share: 22, color: "info", count: "268 Tours" },
  { name: "Nightlife Guide", share: 18, color: "warning", count: "215 Tours" },
  { name: "Photography Guide", share: 14, color: "success", count: "170 Tours" },
  { name: "Adventure & Hiking", share: 10, color: "danger", count: "122 Tours" },
  { name: "Culture & Luxury", share: 8, color: "default", count: "98 Tours" },
];

const CITY_PERFORMANCE = [
  { city: "Tokyo, Japan", bookings: 420, revenue: "$38,400", avgRating: 4.94, guides: 248 },
  { city: "Paris, France", bookings: 385, revenue: "$34,650", avgRating: 4.92, guides: 312 },
  { city: "New York, USA", bookings: 310, revenue: "$29,450", avgRating: 4.88, guides: 285 },
  { city: "Rome, Italy", bookings: 275, revenue: "$22,000", avgRating: 4.91, guides: 195 },
  { city: "Barcelona, Spain", bookings: 240, revenue: "$18,240", avgRating: 4.89, guides: 180 },
  { city: "Dubai, UAE", bookings: 190, revenue: "$24,700", avgRating: 4.95, guides: 175 },
];

const RATING_FACTORS = [
  { factor: "Safety & Identity Trust", score: 4.95, reviews: 2420 },
  { factor: "Friendliness & Hospitality", score: 4.90, reviews: 2420 },
  { factor: "Local Knowledge & Hidden Spots", score: 4.88, reviews: 2420 },
  { factor: "Communication & Promptness", score: 4.85, reviews: 2420 },
  { factor: "Value for Money", score: 4.78, reviews: 2420 },
  { factor: "Overall Experience", score: 4.89, reviews: 2420 },
];

const AnalyticsManagement = () => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("30days");

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      setLoading(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [dateRange]);

  const handleExportData = () => {
    Swal.fire({
      title: "Exporting Analytics Report",
      text: `Exporting CSV metrics for period: ${dateRange}`,
      icon: "success",
      timer: 2000,
      showConfirmButton: false,
    });
  };

  return (
    <>
      <CommonHeader />
      <Container className="mt--7" fluid>
        {/* Metric Overview Cards */}
        <Row className="mb-4">
          <Col lg="3" md="6">
            <Card className="card-stats mb-4 mb-xl-0 shadow-sm border-0">
              <CardBody>
                <Row>
                  <div className="col">
                    <h5 className="text-uppercase text-muted mb-0 font-weight-bold">
                      Platform GMV
                    </h5>
                    <span className="h2 font-weight-bold mb-0">$148,250</span>
                  </div>
                  <Col className="col-auto">
                    <div className="icon icon-shape bg-success text-white rounded-circle shadow">
                      <FaDollarSign />
                    </div>
                  </Col>
                </Row>
                <p className="mt-3 mb-0 text-muted text-sm">
                  <span className="text-success mr-2 font-weight-bold">+24.5%</span>
                  <span>vs previous period</span>
                </p>
              </CardBody>
            </Card>
          </Col>
          <Col lg="3" md="6">
            <Card className="card-stats mb-4 mb-xl-0 shadow-sm border-0">
              <CardBody>
                <Row>
                  <div className="col">
                    <h5 className="text-uppercase text-muted mb-0 font-weight-bold">
                      Total Completed Tours
                    </h5>
                    <span className="h2 font-weight-bold mb-0">1,820 Tours</span>
                  </div>
                  <Col className="col-auto">
                    <div className="icon icon-shape bg-primary text-white rounded-circle shadow">
                      <FaCompass />
                    </div>
                  </Col>
                </Row>
                <p className="mt-3 mb-0 text-muted text-sm">
                  <span className="text-primary mr-2 font-weight-bold">+18.0%</span>
                  <span>Tour bookings</span>
                </p>
              </CardBody>
            </Card>
          </Col>
          <Col lg="3" md="6">
            <Card className="card-stats mb-4 mb-xl-0 shadow-sm border-0">
              <CardBody>
                <Row>
                  <div className="col">
                    <h5 className="text-uppercase text-muted mb-0 font-weight-bold">
                      Guide Subscriptions
                    </h5>
                    <span className="h2 font-weight-bold mb-0">1,240 Active</span>
                  </div>
                  <Col className="col-auto">
                    <div className="icon icon-shape bg-warning text-white rounded-circle shadow">
                      <FaCrown />
                    </div>
                  </Col>
                </Row>
                <p className="mt-3 mb-0 text-muted text-sm">
                  <span className="text-warning mr-2 font-weight-bold">92%</span>
                  <span>Retention rate</span>
                </p>
              </CardBody>
            </Card>
          </Col>
          <Col lg="3" md="6">
            <Card className="card-stats mb-4 mb-xl-0 shadow-sm border-0">
              <CardBody>
                <Row>
                  <div className="col">
                    <h5 className="text-uppercase text-muted mb-0 font-weight-bold">
                      Avg Traveler Rating
                    </h5>
                    <span className="h2 font-weight-bold mb-0">4.89 ★</span>
                  </div>
                  <Col className="col-auto">
                    <div className="icon icon-shape bg-info text-white rounded-circle shadow">
                      <FaStar />
                    </div>
                  </Col>
                </Row>
                <p className="mt-3 mb-0 text-muted text-sm">
                  <span className="text-success mr-2 font-weight-bold">98.2%</span>
                  <span>Positive reviews</span>
                </p>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Filter Bar */}
        <Row className="mb-4">
          <Col>
            <Card className="shadow border-0 p-3">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <h4 className="mb-0 text-dark font-weight-bold">Localmate Performance Insights</h4>
                  <small className="text-muted">Real-time marketplace telemetry and metrics</small>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Input
                    type="select"
                    style={{ width: "180px" }}
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    className="custom-select"
                  >
                    <option value="7days">Last 7 Days</option>
                    <option value="30days">Last 30 Days</option>
                    <option value="90days">Last 3 Months</option>
                    <option value="year">This Year</option>
                  </Input>
                  <Button color="primary" onClick={handleExportData} className="d-flex align-items-center">
                    <FaDownload className="mr-1" /> Export Report
                  </Button>
                </div>
              </div>
            </Card>
          </Col>
        </Row>

        {loading ? (
          <div className="py-5 text-center">
            <LoadingDots message="Computing analytics metrics..." />
          </div>
        ) : (
          <>
            {/* Charts & Breakdown Row */}
            <Row className="mb-4">
              {/* Category Breakdown */}
              <Col lg="6">
                <Card className="shadow border-0 h-100">
                  <CardHeader className="border-0 bg-transparent">
                    <h4 className="mb-0 text-dark font-weight-bold">Guide Categories Popularity</h4>
                    <small className="text-muted">Top booking categories chosen by travelers</small>
                  </CardHeader>
                  <CardBody>
                    {CATEGORY_DISTRIBUTION.map((cat, idx) => (
                      <div key={idx} className="mb-3">
                        <div className="d-flex justify-content-between mb-1">
                          <span className="font-weight-bold text-dark">{cat.name}</span>
                          <span className="text-muted small">{cat.count} ({cat.share}%)</span>
                        </div>
                        <Progress value={cat.share} color={cat.color} style={{ height: "8px" }} />
                      </div>
                    ))}
                  </CardBody>
                </Card>
              </Col>

              {/* 6 Rating Factors Breakdown */}
              <Col lg="6">
                <Card className="shadow border-0 h-100">
                  <CardHeader className="border-0 bg-transparent">
                    <h4 className="mb-0 text-dark font-weight-bold">6-Factor Rating Performance</h4>
                    <small className="text-muted">Post-tour feedback breakdown across safety, knowledge & value</small>
                  </CardHeader>
                  <CardBody>
                    {RATING_FACTORS.map((factor, idx) => (
                      <div key={idx} className="mb-3">
                        <div className="d-flex justify-content-between mb-1">
                          <span className="font-weight-bold text-dark">{factor.factor}</span>
                          <span className="text-primary font-weight-bold">{factor.score} ★ / 5.0</span>
                        </div>
                        <Progress
                          value={(factor.score / 5) * 100}
                          color="success"
                          style={{ height: "8px" }}
                        />
                      </div>
                    ))}
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* City Performance Table */}
            <Row>
              <Col>
                <Card className="shadow border-0">
                  <CardHeader className="border-0">
                    <h4 className="mb-0 text-dark font-weight-bold">Top Performing Destination Cities</h4>
                    <small className="text-muted">Bookings volume, guide density & GMV per destination</small>
                  </CardHeader>
                  <Table className="align-items-center table-flush" responsive hover>
                    <thead className="thead-light">
                      <tr>
                        <th scope="col">Destination City</th>
                        <th scope="col">Completed Tours</th>
                        <th scope="col">Total GMV</th>
                        <th scope="col">Active Guides</th>
                        <th scope="col">Average Rating</th>
                        <th scope="col">Performance Index</th>
                      </tr>
                    </thead>
                    <tbody>
                      {CITY_PERFORMANCE.map((city, idx) => (
                        <tr key={idx}>
                          <td>
                            <span className="font-weight-bold text-primary">
                              <FaMapMarkerAlt className="mr-1 text-danger" /> {city.city}
                            </span>
                          </td>
                          <td>
                            <span className="font-weight-500">{city.bookings} Bookings</span>
                          </td>
                          <td>
                            <span className="font-weight-bold text-success">{city.revenue}</span>
                          </td>
                          <td>
                            <Badge color="info" pill className="p-2">
                              {city.guides} Verified Locals
                            </Badge>
                          </td>
                          <td>
                            <span className="font-weight-bold text-dark">
                              <FaStar className="text-warning mr-1" /> {city.avgRating}
                            </span>
                          </td>
                          <td>
                            <Badge color="success" className="p-2">
                              High Growth
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </>
  );
};

export default AnalyticsManagement;
